import type { Bounds, Camera, Size, Vec2 } from '@/types';
import { visibleWorldBounds } from '@/core/coordinates/coordinateSystem';
import { union, expand } from '@/core/geometry/bounds';

export interface MinimapModel {
  /** World region shown by the minimap (content ∪ viewport, padded). */
  readonly world: Bounds;
  /** Uniform world→minimap scale. */
  readonly scale: number;
  /** Minimap-pixel offset so `world` is centred in the minimap box. */
  readonly offset: Vec2;
  /** Viewport rectangle in minimap pixels. */
  readonly viewport: Bounds;
}

/**
 * Pure mapping for the minimap. The shown region always contains both the content and
 * the current viewport so the user can see where they are relative to everything, and
 * it is letter-boxed inside the minimap box with a uniform scale.
 */
export const computeMinimapModel = (
  content: Bounds | null,
  camera: Camera,
  viewport: Size,
  box: Size,
  paddingPx = 8,
): MinimapModel => {
  const view = visibleWorldBounds(camera, viewport);
  const region = expand(content ? union(content, view) : view, 0);
  const w = Math.max(1, region.maxX - region.minX);
  const h = Math.max(1, region.maxY - region.minY);
  const scale = Math.min((box.width - paddingPx * 2) / w, (box.height - paddingPx * 2) / h);
  const offset = {
    x: (box.width - w * scale) / 2 - region.minX * scale,
    y: (box.height - h * scale) / 2 - region.minY * scale,
  };
  const toMap = (b: Bounds): Bounds => ({
    minX: b.minX * scale + offset.x,
    minY: b.minY * scale + offset.y,
    maxX: b.maxX * scale + offset.x,
    maxY: b.maxY * scale + offset.y,
  });
  return { world: region, scale, offset, viewport: toMap(view) };
};

export const minimapToWorld = (model: MinimapModel, p: Vec2): Vec2 => ({
  x: (p.x - model.offset.x) / model.scale,
  y: (p.y - model.offset.y) / model.scale,
});

export const worldToMinimap = (model: MinimapModel, b: Bounds): Bounds => ({
  minX: b.minX * model.scale + model.offset.x,
  minY: b.minY * model.scale + model.offset.y,
  maxX: b.maxX * model.scale + model.offset.x,
  maxY: b.maxY * model.scale + model.offset.y,
});
